import { Theme, Typography } from "@mui/material";
import parseHTML from "html-react-parser";
import { ReactElement, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { Checkboxes } from "../../common/components/Checkboxes/Checkboxes";
import { Fieldset } from "../../common/components/Fieldset/Fieldset";
import { RadioButtons } from "../../common/components/RadioButtons/RadioButtons";
import { Slider } from "../../common/components/Slider/Slider";
import { Tooltip } from "../../common/components/Tooltip/Tooltip";
import {
  fieldContainerSelectorID,
  footerSelectorID,
} from "../../common/Constants";
import { AnswerOption, Question } from "../../common/types/QuestionnaireEntity";
import { SelectableItem } from "../../common/types/SelectableItem";
import { useElementHeight } from "../../hooks/elementHeight";
import { FieldContainer } from "./FieldContainer";

type Props = {
  question: Question;
  existingAnswer?: any;
  updateAnswer: (answer: any) => void;
};

export const QuestionnaireContainer: React.FC<Props> = ({
  question,
  existingAnswer,
  updateAnswer,
}) => {
  const { formatMessage } = useIntl();
  const footerHeightOffset = useElementHeight(footerSelectorID);
  const [response, setResponse] = useState<string | number | undefined>(
    undefined
  );
  const [multiSelect, setMultiSelect] = useState<(string | number)[]>([]);
  const [detailedResponse, setDetailedResponse] = useState<string | undefined>(
    ""
  );
  const [questionText, setQuestionText] = useState<ReactElement>();
  const [prefix, setQuestionPrefix] = useState<ReactElement>();

  useEffect(() => {
    setQuestionText(parseHTML(question.text || "") as ReactElement);
    setQuestionPrefix(parseHTML(question.prefix || "") as ReactElement);
  }, [question]);

  useEffect(() => {
    if (existingAnswer) {
      setResponse(existingAnswer.value.response);
      setDetailedResponse(existingAnswer.value.detailedResponse ?? "");
      setMultiSelect(existingAnswer.value.multiSelect ?? []);
    }
  }, [existingAnswer]);

  useEffect(() => {
    if (!existingAnswer) {
      updateAnswer({ response, multiSelect, detailedResponse });
    } else if (response || multiSelect.length > 0 || detailedResponse !== "") {
      // TODO: find solution that allows empty responses in multiselect that doesn't catch the empty state of first dom render and wipe response
      updateAnswer({ response, multiSelect, detailedResponse });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, multiSelect, detailedResponse]);

  const convertToSelectableItem = (
    option: AnswerOption,
    key: string
  ): SelectableItem => {
    const hint = option.value.Reference?.display;
    const isHint = option.value.Reference?.display.includes("e.g.");
    const isCustom =
      option.value.string.toLowerCase().includes("other") ||
      (hint !== undefined && !isHint);

    return {
      key: key,
      value: option.value.string,
      isCustom: isCustom,
      hint: isHint ? hint : undefined,
      customDataLabel: hint && isCustom ? hint : undefined,
    };
  };

  const isTooltipVisible = (question: any) => {
    if (question.extension && question.extension.length > 0) {
      return question.extension.find((extension: any) =>
        extension.url.includes("questionnaire-instruction")
      );
    }
    return null;
  };

  return (
    <div
      style={{
        marginTop: "47px",
        marginBottom: footerHeightOffset,
      }}
    >
      {question.type === "choice" && (
        <Fieldset
          prefix={prefix || <></>}
          legend={questionText || <></>}
        >
          <FieldContainer
            elementID={fieldContainerSelectorID}
            elementIDToAvoid={footerSelectorID}
          >
            <RadioButtons
              selected={response}
              onSelectedChange={setResponse}
              customValue={detailedResponse}
              onCustomValueChange={setDetailedResponse}
              items={question.answerOption.map((elem, index) => {
                return convertToSelectableItem(elem, elem.value.string);
              })}
            />
          </FieldContainer>
        </Fieldset>
      )}
      {question.type === "choice_slider" && (
        <>
          <Typography
            id="questionnaire-slider-typography"
            sx={(theme: Theme) => ({
              color: theme.palette.common.black,
              fontSize: "1.09rem",
              whiteSpace: "initial",
            })}
          >
            {prefix && (
              <Typography
                component="span"
                id="questionnaire-slider-prefix"
                sx={(theme: Theme) => ({
                  color: theme.palette.common.black,
                  fontSize: ".9rem",
                  whiteSpace: "initial",
                  paddingBottom: 3,
                })}
              >
                {prefix}
              </Typography>
            )}
            {questionText}
          </Typography>
          <Slider
            ariaLabelledby="questionnaire-slider"
            min={0}
            max={question.answerOption.length - 1}
            onValueChange={setResponse}
            value={response}
          />
        </>
      )}
      {(question.type === "open-choice" || question.type === "open_choice") && (
        <Fieldset
          prefix={prefix || <></>}
          legend={questionText || <></>}
          helpComponent={
            isTooltipVisible(question) ? (
              <Tooltip label={formatMessage({ id: "tooltip.label" })}>
                {formatMessage({ id: "tooltip.body" })}
              </Tooltip>
            ) : null
          }
          hint={formatMessage({ id: "checkboxes.hint" })}
        >
          <FieldContainer
            elementID={fieldContainerSelectorID}
            elementIDToAvoid={footerSelectorID}
          >
            <Checkboxes
              selected={multiSelect}
              onSelectedChange={setMultiSelect}
              customValue={detailedResponse}
              onCustomValueChange={setDetailedResponse}
              items={question.answerOption.map((elem, index) => {
                return convertToSelectableItem(elem, question.id + index);
              })}
            />
          </FieldContainer>
        </Fieldset>
      )}
    </div>
  );
};
